// /src/hooks/useWebsiteAccessGate.js
// Identity check + onboarding completion for secondary nav and private routes.
// Viewer onboardingCompletedAt stays the logged-in admin; subject fields are
// loaded when view-as is active so GamerIdentityCheck can preview that user.

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../authContext';
import { useSecurity } from '../SecurityContext';
import {
  canShowSecondaryNavBar,
  isIdentityCheckComplete,
  isOnboardingComplete,
  isOnboardingCompleteFromServer,
  ONBOARDING_COMPLETE_EVENT,
  siteAccessBypassActive,
} from '../utils/onboardingStorage';
import { logOnboardingGateDecision } from '../utils/onboardingGateLog';
import { fetchMemberProfileCard } from '../components/tools/memberProfileCardApi';
import { isOrgMembershipEligible } from '../utils/orgMembershipMessages';

const API_ONBOARDING_STATUS = '/api/user/onboarding/status';
const API_IDENTITY_SNAPSHOT = '/api/user/identity-snapshot';

const WebsiteAccessGateContext = createContext(null);

function mergeSubjectIdentityFromProfileCard(base, card) {
  if (!card || typeof card !== 'object') return base;
  const handlesFromCard = Array.isArray(card.handles)
    ? card.handles
        .filter((h) => h && h.handle)
        .map((h) => ({
          handleId: Number(h.handleId ?? h.id),
          id: Number(h.handleId ?? h.id),
          handle: String(h.handle),
          isMaster: h.isMaster,
        }))
    : [];
  return {
    email: base?.email ?? null,
    emailVerified: base?.emailVerified ?? false,
    anonymizationScheduledAt: base?.anonymizationScheduledAt ?? null,
    discordName: base?.discordName ?? card.discordName ?? null,
    discordId: base?.discordId ?? null,
    discordAvatar: base?.discordAvatar ?? card.discordAvatar ?? null,
    handles: (base?.handles?.length ? base.handles : handlesFromCard) ?? [],
    gcuAccepted: base?.gcuAccepted === true,
  };
}

export function WebsiteAccessGateProvider({ children }) {
  const { email, discordName, handles, gcuAccepted, userId, isLoggedIn } = useAuth();
  const { isViewAsActive, viewAsUserId, isSuperAdmin, viewAsIsSuperAdmin, isGuest, viewAsIsGuest, viewAsLoading } = useSecurity();
  const location = useLocation();

  const [statusLoading, setStatusLoading] = useState(isLoggedIn);
  const [onboardingCompletedAt, setOnboardingCompletedAt] = useState(null);
  const [subjectOnboardingCompletedAt, setSubjectOnboardingCompletedAt] = useState(null);
  const [orgMembership, setOrgMembership] = useState(null);
  const [subjectOrgMembership, setSubjectOrgMembership] = useState(null);
  const [subjectIdentity, setSubjectIdentity] = useState(null);
  const [subjectIdentityUserId, setSubjectIdentityUserId] = useState(null);
  const [statusRevision, setStatusRevision] = useState(0);
  const hasLoadedOnceRef = useRef(false);
  const fetchGenRef = useRef(0);
  const subjectIdentityUserIdRef = useRef(null);

  useEffect(() => {
    subjectIdentityUserIdRef.current = subjectIdentityUserId;
  }, [subjectIdentityUserId]);

  const fetchStatus = useCallback(async () => {
    if (!isLoggedIn) {
      setOnboardingCompletedAt(null);
      setSubjectOnboardingCompletedAt(null);
      setOrgMembership(null);
      setSubjectOrgMembership(null);
      setSubjectIdentity(null);
      setSubjectIdentityUserId(null);
      hasLoadedOnceRef.current = false;
      setStatusLoading(false);
      return;
    }

    const fetchGen = fetchGenRef.current + 1;
    fetchGenRef.current = fetchGen;

    const targetViewAsId = isViewAsActive && viewAsUserId != null
      ? Number(viewAsUserId)
      : null;

    const showLoading = !hasLoadedOnceRef.current
      || (targetViewAsId != null && subjectIdentityUserIdRef.current !== targetViewAsId);
    if (showLoading) setStatusLoading(true);

    try {
      // Viewer onboarding stays skipViewAs so admin nav gates never flip.
      // Subject preview is loaded separately with an explicit viewAsUserId
      // (query + header) so we never accidentally cache the admin identity.
      const statusPromise = axios.get(API_ONBOARDING_STATUS, {
        withCredentials: true,
        skipViewAs: true,
      });

      const subjectStatusPromise = targetViewAsId
        ? axios.get(API_ONBOARDING_STATUS, {
            withCredentials: true,
            params: { viewAsUserId: targetViewAsId },
            headers: { 'X-View-As-UserId': String(targetViewAsId) },
          })
        : null;

      const snapshotPromise = targetViewAsId
        ? axios.get(API_IDENTITY_SNAPSHOT, {
            withCredentials: true,
            params: { viewAsUserId: targetViewAsId },
            headers: { 'X-View-As-UserId': String(targetViewAsId) },
          })
        : null;

      const [statusResult, subjectStatusResult, snapshotResult] = await Promise.allSettled([
        statusPromise,
        subjectStatusPromise ?? Promise.resolve(null),
        snapshotPromise ?? Promise.resolve(null),
      ]);

      if (fetchGen !== fetchGenRef.current) return;

      if (statusResult.status === 'fulfilled') {
        const data = statusResult.value.data;
        setOnboardingCompletedAt(data?.onboardingCompletedAt ?? null);
        setOrgMembership(data?.orgMembership ?? null);

        if (data?.onboardingCompletedAt == null) {
          try {
            window.localStorage.removeItem('membersOnboardingCompleted');
          } catch {
            // private mode / quota
          }
        }
        logOnboardingGateDecision({
          label: 'GET /api/user/onboarding/status response',
          identity: { email, discordName, handles, gcuAccepted },
          onboardingCompletedAt: data?.onboardingCompletedAt ?? null,
          subjectOnboardingCompletedAt: data?.subjectOnboardingCompletedAt ?? null,
          isViewAsActive,
          isSuperAdmin,
          viewAsUserId,
          userId,
          pathname: location.pathname,
          statusLoading: false,
        });
      } else {
        setOnboardingCompletedAt(null);
        setOrgMembership(null);
        console.warn('[onboarding-gate] GET /api/user/onboarding/status failed', statusResult.reason);
      }

      if (!targetViewAsId) {
        setSubjectOnboardingCompletedAt(null);
        setSubjectOrgMembership(null);
        setSubjectIdentity(null);
        setSubjectIdentityUserId(null);
      } else {
        const subjectStatusData =
          subjectStatusResult.status === 'fulfilled'
            ? subjectStatusResult.value?.data
            : null;
        const snapshotData =
          snapshotResult.status === 'fulfilled'
            ? snapshotResult.value?.data
            : null;

        if (subjectStatusResult.status === 'rejected') {
          console.warn(
            '[onboarding-gate] subject onboarding/status failed',
            subjectStatusResult.reason,
          );
        }
        if (snapshotResult.status === 'rejected') {
          console.warn(
            '[onboarding-gate] identity-snapshot failed',
            snapshotResult.reason,
          );
        }

        setSubjectOnboardingCompletedAt(
          subjectStatusData?.subjectOnboardingCompletedAt ?? null,
        );
        setSubjectOrgMembership(
          subjectStatusData?.subjectOrgMembership ?? null,
        );

        let identity =
          snapshotData?.subjectIdentity
          ?? subjectStatusData?.subjectIdentity
          ?? null;

        // Guard: never keep the admin snapshot when view-as id was ignored by the API.
        const snapshotSubjectUserId = snapshotData?.subjectUserId != null
          ? Number(snapshotData.subjectUserId)
          : null;
        if (
          identity
          && snapshotSubjectUserId != null
          && snapshotSubjectUserId !== targetViewAsId
        ) {
          console.warn('[onboarding-gate] identity-snapshot returned wrong user', {
            expected: targetViewAsId,
            got: snapshotSubjectUserId,
          });
          identity = subjectStatusData?.subjectIdentity ?? null;
        }

        if (
          !identity
          || (!identity.email && !identity.discordName && !(identity.handles?.length > 0))
        ) {
          try {
            const cardRes = await fetchMemberProfileCard(targetViewAsId);
            if (fetchGen !== fetchGenRef.current) return;
            if (cardRes?.ok && cardRes.card) {
              identity = mergeSubjectIdentityFromProfileCard(identity, cardRes.card);
            }
          } catch {
            // keep API identity only
          }
        }

        setSubjectIdentity(identity);
        setSubjectIdentityUserId(identity ? targetViewAsId : null);
      }
    } catch (err) {
      if (fetchGen !== fetchGenRef.current) return;
      setOnboardingCompletedAt(null);
      setSubjectOnboardingCompletedAt(null);
      setOrgMembership(null);
      setSubjectOrgMembership(null);
      setSubjectIdentity(null);
      setSubjectIdentityUserId(null);
      console.warn('[onboarding-gate] fetchStatus error', err);
    } finally {
      if (fetchGen !== fetchGenRef.current) return;
      hasLoadedOnceRef.current = true;
      setStatusLoading(false);
    }
  }, [
    isLoggedIn,
    isViewAsActive,
    viewAsUserId,
    email,
    discordName,
    handles,
    gcuAccepted,
    isSuperAdmin,
    userId,
    location.pathname,
  ]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus, isViewAsActive, viewAsUserId, statusRevision]);

  useEffect(() => {
    const onCompleted = (e) => {
      const at = e?.detail?.onboardingCompletedAt;
      if (at) {
        setOnboardingCompletedAt(at);
        if (isViewAsActive) {
          setSubjectOnboardingCompletedAt(at);
        }
      }
      setStatusRevision((n) => n + 1);
    };
    window.addEventListener(ONBOARDING_COMPLETE_EVENT, onCompleted);
    return () => window.removeEventListener(ONBOARDING_COMPLETE_EVENT, onCompleted);
  }, [isViewAsActive]);

  const subjectIdentityReady = !isViewAsActive
    || (
      viewAsUserId != null
      && subjectIdentityUserId === Number(viewAsUserId)
      && subjectIdentity != null
    );

  const effectiveIdentity = useMemo(() => {
    if (isViewAsActive) {
      if (subjectIdentityReady) return subjectIdentity;
      // Don't fall back to the admin auth identity while the subject loads.
      return { email: null, discordName: null, handles: [], gcuAccepted: false };
    }
    return { email, discordName, handles, gcuAccepted };
  }, [
    isViewAsActive,
    subjectIdentityReady,
    subjectIdentity,
    email,
    discordName,
    handles,
    gcuAccepted,
  ]);

  const effectiveIsSuperAdmin = isViewAsActive ? viewAsIsSuperAdmin : isSuperAdmin;
  const effectiveIsGuest = isViewAsActive ? viewAsIsGuest : isGuest;
  const accessBypass = siteAccessBypassActive({
    isSuperAdmin: effectiveIsSuperAdmin,
    isGuest: effectiveIsGuest,
  });

  const identityComplete = isIdentityCheckComplete(effectiveIdentity);

  const onboardingComplete = statusLoading
    ? (
      (isViewAsActive && isOnboardingCompleteFromServer(subjectOnboardingCompletedAt))
      || isOnboardingCompleteFromServer(onboardingCompletedAt)
      || isOnboardingComplete()
    )
    : (
      (isViewAsActive && isOnboardingCompleteFromServer(subjectOnboardingCompletedAt))
      || isOnboardingCompleteFromServer(onboardingCompletedAt)
    );

  const effectiveOrgMembership = isViewAsActive
    ? subjectOrgMembership
    : orgMembership;

  const orgMembershipEligible = useMemo(() => {
    if (accessBypass) return true;
    return isOrgMembershipEligible(effectiveOrgMembership, effectiveIsSuperAdmin);
  }, [effectiveOrgMembership, effectiveIsSuperAdmin, accessBypass]);

  const navUnlocked = useMemo(
    () =>
      canShowSecondaryNavBar(
        effectiveIdentity,
        effectiveIsSuperAdmin,
        onboardingComplete,
        orgMembershipEligible,
        effectiveIsGuest,
      ),
    [effectiveIdentity, effectiveIsSuperAdmin, onboardingComplete, orgMembershipEligible, effectiveIsGuest],
  );

  const needsOnboardingRedirect =
    identityComplete
    && orgMembershipEligible
    && !onboardingComplete
    && !accessBypass;

  const gateLoading = statusLoading
    || (isViewAsActive && (viewAsLoading || !subjectIdentityReady));

  useEffect(() => {
    if (gateLoading) return;
    logOnboardingGateDecision({
      label: 'useWebsiteAccessGate computed (after load)',
      identity: effectiveIdentity,
      onboardingCompletedAt,
      subjectOnboardingCompletedAt,
      isViewAsActive,
      isSuperAdmin: effectiveIsSuperAdmin,
      viewAsUserId,
      userId,
      pathname: location.pathname,
      statusLoading: gateLoading,
    });
  }, [
    gateLoading,
    effectiveIdentity,
    onboardingCompletedAt,
    subjectOnboardingCompletedAt,
    isViewAsActive,
    effectiveIsSuperAdmin,
    viewAsUserId,
    userId,
    location.pathname,
    identityComplete,
    onboardingComplete,
    needsOnboardingRedirect,
  ]);

  const value = useMemo(
    () => ({
      statusLoading: gateLoading,
      isViewAsActive,
      viewAsPreview: isViewAsActive,
      subjectIdentity,
      subjectIdentityReady,
      effectiveIdentity,
      effectiveIsSuperAdmin,
      effectiveIsGuest,
      identityComplete,
      onboardingComplete,
      orgMembership: effectiveOrgMembership,
      orgMembershipEligible,
      navUnlocked,
      needsOnboardingRedirect,
      refetchStatus: fetchStatus,
    }),
    [
      gateLoading,
      isViewAsActive,
      subjectIdentity,
      subjectIdentityReady,
      effectiveIdentity,
      effectiveIsSuperAdmin,
      effectiveIsGuest,
      identityComplete,
      onboardingComplete,
      effectiveOrgMembership,
      orgMembershipEligible,
      navUnlocked,
      needsOnboardingRedirect,
      fetchStatus,
    ],
  );

  return (
    <WebsiteAccessGateContext.Provider value={value}>
      {children}
    </WebsiteAccessGateContext.Provider>
  );
}

export function useWebsiteAccessGate() {
  const ctx = useContext(WebsiteAccessGateContext);
  if (!ctx) {
    throw new Error('useWebsiteAccessGate must be used within WebsiteAccessGateProvider');
  }
  return ctx;
}
